// ─────────────────────────────────────────────────────────────────────────────
// WP-15 REQ-128 — Sección "Puntos" del Admin.
//
// PARIDAD 1:1 CON LA MAQUETA (fundamentales-admin.js, bodies.puntos): dos tablas apiladas, Front
// Line primero y Staff después, cada una con su propio toggle "— Activo", y una fila de toggles
// sobre el título con el interruptor GLOBAL. Consume RulesEngine.
//
// Tres paquetes conviven aquí porque tocan el mismo fichero y separarlos serían tres conflictos:
//   · WP-A3 (D-1) — el toggle GLOBAL "Aplican puntos", solo AdminGlobal.
//   · WP-A4 (D-2) — la 5.ª columna "Total Acumulado": conteo de NOMINACIONES por criterio.
//   · WP-A8 (D-11b) — selector de OU, que antes era `demoUser.ou` fijo.
//
// REGLA DE ORO: los puntos NUNCA se muestran al empleado — esta pantalla es admin-gated (AdminView).
// La edición se DESHABILITA si hay una campaña ACTIVA para la OU en pantalla (Bug 03,
// `useActiveCampaign(ouId)`, reforzada por `campaignStarted` si el backend lo trae). CERO mocks —
// estados loading/error reales.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  usePointsConfig,
  useSavePointsConfig,
  usePointsSummary,
  usePointsToggle,
  usePointsGlobalRule,
  useSetGlobalPointsEnabled,
  usePointsVisibleRule,
  useSetOuPointsVisibility,
  useResetPointsConfigAccumulated,
  useActiveCampaign,
  getRecognitionErrorMessage,
  rulesApi,
} from '@/api/recognition';
import { ConfirmModal } from '@/components/common';
import type { OUItem } from '@/api/types';
import { useUnsavedChangesGuard } from '@/contexts/UnsavedChangesContext';
import type {
  PointsConfigDto,
  PointsSegment,
  PointCriterionDto,
  CriterioNominationCount,
  PointsExportRowDto,
} from '@/api/recognition';
import { useUserContext, isRecognitionAdmin } from '@/contexts/UserContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useOUItems } from '@/hooks/useOUs';
import { userAdminApi } from '@/api/client';
import { exportToExcel } from '@/utils/exportExcel';
import { construirFilasReportePuntos, resolverNombresDePersonas } from './pointsExportRows';
import { StateBlock } from '../components';
import { AdminSection, AdminSaveBar } from './adminShared';

export function AdminPoints() {
  const { t } = useTranslation('recognition');
  const { demoUser } = useUserContext();
  const currentUser = useCurrentUser();

  // WP-A8 — el alcance del selector sale del PERFIL, no del rol pintado en el front: `allOusAccess`
  // es lo que el backend usa para decidir, y aquí solo se refleja. Si el perfil aún no resolvió se
  // asume lo restrictivo (su propia OU) en vez de enseñar el árbol entero por defecto.
  // `recognitionOuScope` (Brillamos), NO `ouScope` (Pulso) — mismo criterio que AdminCampaigns
  // (fix 2026-08-12).
  const ouScope = currentUser?.profileResolved ? currentUser.recognitionOuScope : undefined;
  const puedeElegirOu = ouScope?.allOusAccess ?? false;
  const esAdminGlobal = currentUser?.roles?.includes('AdminGlobal') ?? false;

  // WP-A6 / RF-066 — mismos roles que autoriza el backend en `GET v1/rules/points-export`
  // (AdminGlobal, AdminBrillamos, AdminOUBrillamos): se reutiliza `isRecognitionAdmin`, la MISMA
  // fuente única que ya usan `useRecognitionPermissions` y el resto del Admin, en vez de repetir
  // la lista de roles suelta por tercera vez en el front.
  const puedeExportarPuntos = isRecognitionAdmin(currentUser?.roles ?? []);

  // Matriz de permisos de Reconocimiento — "Exportar datos" (RN-K03) gatea el botón aparte de
  // "Gestión"/"Puntos", que ya autoriza el resto de la pantalla. `?.` en cascada: mientras
  // el perfil resuelve, falla CERRADO (botón oculto), nunca abierto por defecto.
  const canExportData = currentUser?.recognitionPermissions?.includes('exportData') ?? false;

  const propiaOu = demoUser.ou || undefined;

  // 🔴 `undefined` inicial A PROPÓSITO, no `propiaOu`.
  //
  // `demoUser.ou` arranca vacío y se hidrata cuando el perfil resuelve, así que sembrar el estado
  // con él hacía que el valor inicial dependiera de una CARRERA: con la caché caliente quedaba
  // preseleccionado y con el navegador limpio se quedaba vacío para siempre. El efecto de abajo lo
  // siembra cuando el dato llega de verdad.
  const [ouElegida, setOuElegida] = useState<string | undefined>(undefined);

  useEffect(() => {
    // El admin no global queda CLAVADO a su OU, resuelva cuando resuelva.
    if (!puedeElegirOu) { setOuElegida(propiaOu); return; }
    // El global arranca en la suya como cortesía, pero solo mientras no haya elegido nada: si ya
    // eligió, un refresco del perfil no puede moverlo de OU sin avisar.
    setOuElegida((actual) => actual ?? propiaOu);
  }, [puedeElegirOu, propiaOu]);

  const ouId = puedeElegirOu ? ouElegida : propiaOu;

  // Distingue "todavía no has elegido" de "tu ficha no tiene unidad y no puedes elegir": para el
  // segundo, pedirle que seleccione una es una instrucción imposible de cumplir.
  const sinAlcancePropio = !puedeElegirOu && !propiaOu;

  // Bug 03 — la fuente de verdad de "¿se puede editar puntos aquí?" pasa a ser la campaña ACTIVA
  // de la OU en pantalla, con el MISMO hook que ya usan BrillamosView/PerfilView (`useActiveCampaign`,
  // GET v1/campaigns/active?ou=), no solo `campaignStarted` de `PointsConfigDto`.
  //
  // `campaignStarted` vive en RulesEngine, un servicio DISTINTO del que administra campañas
  // (Reconocimiento, dentro de SurveyService), así que no hay garantía de que esté sincronizado con
  // la campaña real de la OU. Peor: en desarrollo local `rulesApi.getPointsConfig` ni siquiera pega
  // al backend — devuelve `DEV_MOCK_POINTS_CONFIG` (`client.ts`), que trae `campaignStarted: false`
  // HARDCODEADO sin importar el estado real. Cualquiera de las dos causas deja la pantalla editable
  // con una campaña activa.
  //
  // Se combina con `campaignStarted` (no se descarta) dentro de `PointsSegmentPanel`: cualquiera de
  // las dos señales bloquea, así que si el backend algún día sincroniza ese campo no se pierde
  // protección, solo se refuerza.
  const activeCampaign = useActiveCampaign(ouId);
  const campaignLocked = Boolean(activeCampaign.data);

  return (
    <AdminSection
      title={t('admin.points.title')}
      description={t('admin.points.desc')}
      headerNote={ouId && campaignLocked ? (
        <p className="adm-section-warning" role="status">
          <i className="fas fa-triangle-exclamation" aria-hidden="true" />
          {' '}
          {t('admin.points.campaignActiveWarning')}
        </p>
      ) : undefined}
    >
      {/*
        WP-A6 / RF-066 — primer consumidor del reporte. NO depende de haber elegido una OU: sin
        `ouId` un AdminGlobal exporta el alcance global completo (cascadeado por el backend), que
        es justamente el caso de uso principal de "Tabla/reporte de puntos" de la spec.
      */}
      {puedeExportarPuntos && canExportData && <PointsExportButton ouId={ouId} />}

      {/*
        Solo se monta para AdminGlobal, y así el hook del toggle ni siquiera se ejecuta para los
        demás: tenerlo dentro con un `return null` posterior disparaba una petición para no pintar
        nada.
      */}
      {esAdminGlobal && <GlobalPointsToggle ouId={ouId} locked={campaignLocked} />}
      {esAdminGlobal && ouId && <VisibleParaEmpleadosToggle ouId={ouId} locked={campaignLocked} />}

      {puedeElegirOu && <OuPicker value={ouElegida} onChange={setOuElegida} />}

      {/*
        🔴 Sin OU no se pinta NADA editable.
        La configuración de puntos es SIEMPRE por OU+segmento: no existe una "config global" que
        editar. Cuando `ouId` era undefined, `usePointsConfig` quedaba deshabilitado, el efecto de
        sincronización salía por `if (!query.data) return` y las tablas seguían mostrando —y dejando
        editar— la configuración de la OU ANTERIOR, mientras Guardar no hacía nada y no decía nada.
        Un admin sin OU en su ficha arrancaba directamente en ese estado.
      */}
      {ouId ? (
        <>
          <PointsSegmentPanel ouId={ouId} segment="frontline" campaignLocked={campaignLocked} />
          <PointsSegmentPanel ouId={ouId} segment="staff" campaignLocked={campaignLocked} />
        </>
      ) : (
        <p className="adm-note" role="status">
          {t(sinAlcancePropio ? 'admin.points.noOuInProfile' : 'admin.points.pickOuFirst')}
        </p>
      )}
    </AdminSection>
  );
}

// ─── WP-A6 / RF-066 · botón "Descargar reporte de puntos" ─────────────────────

interface PointsExportButtonProps {
  /**
   * OU actualmente en pantalla. Es una PETICIÓN, igual que en `usePointsSummary`/`getPointsSummary`:
   * el backend confina a un admin no global a la suya sin importar lo que se mande. `undefined` no
   * es "sin resultado" — es el caso que exporta el alcance GLOBAL completo para un AdminGlobal.
   */
  ouId: string | undefined;
}

/** Tope de página que el servidor acepta (`GET v1/rules/points-export`, contrato verificado). */
const POINTS_EXPORT_PAGE_SIZE = 1000;

/**
 * Tope duro de filas del recorrido, con aviso si se alcanza — mismo espíritu que
 * `AdminWinners.exportar()` (ahí 5000, sobre ganadores). El universo aquí es el headcount COMPLETO
 * con puntos configurados en el alcance, potencialmente el organigrama entero en el caso global,
 * así que se fija diez veces más alto: cortar en silencio a media plantilla sería peor que la
 * descarga lenta de un reporte grande.
 */
const POINTS_EXPORT_ROW_CAP = 50000;

/**
 * WP-A6 / RF-066 — primer consumidor de `GET v1/rules/points-export`. El endpoint YA EXISTÍA y
 * funcionaba en el backend; hasta esta tarea nadie podía descargarlo desde el front.
 *
 * Tabla de solo lectura (Persona/OU/Puntos): una hoja plana con `exportToExcel`, no el generador
 * multi-hoja de la Tarea A3 (`exportMultiSheetExcel`) — son 3 columnas sin agrupaciones que
 * justifiquen varias pestañas.
 *
 * Mismo patrón de paginado-con-tope que `AdminWinners.exportar()`: el endpoint acota `pageSize` a
 * 1000, así que se recorren páginas hasta juntar `total` filas o hasta el tope duro, lo que llegue
 * primero. Un aviso reemplaza al truncamiento silencioso.
 */
function PointsExportButton({ ouId }: Readonly<PointsExportButtonProps>) {
  const { t } = useTranslation('recognition');
  const [exportando, setExportando] = useState(false);
  const [exportError, setExportError] = useState(false);
  const [exportVacio, setExportVacio] = useState(false);
  const [exportTruncado, setExportTruncado] = useState(false);
  const [exportSinNombres, setExportSinNombres] = useState(false);

  // 🔴 `rootsOnly: false` y `activeOnly: false`, a diferencia del selector de OU de esta misma
  // pantalla. El reporte trae la OU EXACTA en la que se contabilizó a cada persona, que puede ser
  // una hija de cuarto nivel o una unidad ya desactivada; con el catálogo recortado, esas filas se
  // quedarían con el identificador crudo, que es justo lo que este cambio viene a quitar.
  const { data: ous = [] } = useOUItems({ rootsOnly: false, activeOnly: false });

  const exportar = async () => {
    setExportando(true);
    setExportError(false);
    setExportVacio(false);
    setExportTruncado(false);
    setExportSinNombres(false);
    try {
      const todas: PointsExportRowDto[] = [];
      let pagina = 1;
      let total = 0;

      do {
        const res = await rulesApi.exportPersonPoints(ouId, pagina, POINTS_EXPORT_PAGE_SIZE);
        // Una página VACÍA corta el bucle aunque `total` diga que faltan filas: sin esto, si los
        // datos cambian entre dos peticiones el recorrido no avanza y pide páginas para siempre.
        if (res.filas.length === 0) break;
        todas.push(...res.filas);
        total = res.total;
        pagina += 1;
      } while (todas.length < total && todas.length < POINTS_EXPORT_ROW_CAP);

      if (todas.length < total) setExportTruncado(true);

      if (todas.length === 0) {
        setExportVacio(true);
        return;
      }

      // El backend solo sabe de identificadores: el directorio y el catálogo de OUs viven en
      // UserAdminService y RulesEngine no puede leerlos (regla de oro #2). La traducción se hace
      // aquí, que es donde las dos fuentes están a mano.
      const { nombres, completo } = await resolverNombresDePersonas(
        todas.map((fila) => fila.personaId),
        userAdminApi.batchLookupUsers,
      );
      if (!completo) setExportSinNombres(true);

      await exportToExcel(
        construirFilasReportePuntos(
          todas,
          {
            nombrePorPersona: nombres,
            nombrePorOu: new Map(ous.map((ou) => [ou.id, ou.name])),
          },
          {
            persona: t('admin.points.exportColPerson'),
            unidad: t('admin.points.exportColOu'),
            puntos: t('admin.points.exportColPoints'),
          },
        ),
        `reporte-puntos-${new Date().toISOString().slice(0, 10)}`,
      );
    } catch {
      // Si falla, se DICE: sin esto el botón se rehabilita, no aparece ningún fichero y el
      // administrador no tiene forma de saber si descargó algo o no.
      setExportError(true);
    } finally {
      setExportando(false);
    }
  };

  return (
    <div className="adm-toggles-row">
      <button
        type="button"
        className="btn btn--success"
        disabled={exportando}
        onClick={exportar}
      >
        <i className={exportando ? 'fas fa-spinner fa-spin' : 'fas fa-download'} aria-hidden />{' '}
        {exportando ? t('admin.points.exporting') : t('admin.points.exportReport')}
      </button>

      {exportError && (
        <p className="rp-error" role="alert">{t('admin.points.exportError')}</p>
      )}
      {exportVacio && (
        <p className="adm-note" role="status">{t('admin.points.exportEmpty')}</p>
      )}
      {exportTruncado && (
        <p className="adm-note" role="status">
          {t('admin.points.exportTruncated', { tope: POINTS_EXPORT_ROW_CAP })}
        </p>
      )}
      {exportSinNombres && (
        <p className="adm-note" role="status">{t('admin.points.exportNamesUnresolved')}</p>
      )}
    </div>
  );
}

// ─── WP-A8 · selector de OU ───────────────────────────────────────────────────

interface OuPickerProps {
  value: string | undefined;
  onChange: (ouId: string | undefined) => void;
}

/**
 * WP-A8 / CPE-120 — antes la pantalla usaba `demoUser.ou` fijo, así que un AdminGlobal **no podía
 * ver ni editar otra OU**. No era solo un caso de prueba en rojo: era una limitación funcional.
 *
 * Se pinta un `select` plano y no el `OuCascadeSelect` de Alcance: aquel es MULTI-selección con
 * cascada a descendientes, y aquí se elige UNA OU. Reutilizarlo obligaría a tratar su `string[]`
 * como si fuera un valor único, que es la clase de forzado que después nadie entiende.
 */
function OuPicker({ value, onChange }: Readonly<OuPickerProps>) {
  const { t } = useTranslation('recognition');
  const { data: ous = [], isLoading } = useOUItems();

  // 🔴 TODOS los niveles, no solo las raíces.
  //
  // Una versión anterior filtraba a `!parentOuId` "porque las OUs de primer nivel son las que tienen
  // configuración". Es falso, y el modo de fallo era grave: el motor busca la config por la OU
  // EXACTA del emisor (`points_config` se llavea `{ouId}::{segmento}` y el asiento guarda
  // `evt.EmisorOuId`), sin expandir a ascendientes. Si la gente cuelga de OUs hijas —el organigrama
  // tiene cuatro niveles— entonces:
  //   · la tabla que el admin editaba para la raíz NO la leía nadie, y
  //   · la columna "Total acumulado" salía en ceros, porque el conteo también filtra por igualdad.
  // Las dos cosas en silencio, sin ningún error.
  //
  // Se ordena por jerarquía para que la lista siga siendo legible: cada hija debajo de su madre.
  const ordenadas = useMemo(() => ordenarPorJerarquia(ous), [ous]);

  return (
    <div className="adm-toggles-row">
      <label className="adm-field">
        <span className="adm-field-label">{t('admin.points.ouPicker')}</span>
        <select
          className="adm-select"
          value={value ?? ''}
          disabled={isLoading}
          onChange={(e) => onChange(e.target.value || undefined)}
        >
          {/*
            NO hay opción "todas las OU": la config de puntos es siempre por OU+segmento y no existe
            una global que editar. Ofrecerla dejaba la pantalla en un estado donde las tablas
            mostraban la OU anterior y Guardar no hacía nada.
          */}
          <option value="">{t('admin.points.ouPickPlaceholder')}</option>
          {ordenadas.map(({ ou, nivel }) => (
            <option key={ou.id} value={ou.id}>
              {'  '.repeat(nivel)}{ou.name}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

/**
 * Aplana el árbol de OUs en el orden en que se lee: cada madre seguida de sus hijas, y todas
 * ordenadas alfabéticamente dentro de su nivel. Devuelve el nivel para poder sangrar.
 *
 * Las OUs huérfanas —cuya madre no está en el catálogo, p. ej. si viene filtrado— se tratan como
 * raíces en vez de descartarse: perder una OU del selector significa que su configuración de puntos
 * se vuelve ineditable, y eso es peor que enseñarla sin sangría.
 */
function ordenarPorJerarquia(ous: OUItem[]): { ou: OUItem; nivel: number }[] {
  const porMadre = new Map<string, OUItem[]>();
  const ids = new Set(ous.map((o) => o.id));

  for (const ou of ous) {
    const madre = ou.parentOuId && ids.has(ou.parentOuId) ? ou.parentOuId : '';
    porMadre.set(madre, [...(porMadre.get(madre) ?? []), ou]);
  }

  const salida: { ou: OUItem; nivel: number }[] = [];
  const visitados = new Set<string>();

  const recorrer = (madre: string, nivel: number) => {
    const hijas = [...(porMadre.get(madre) ?? [])].sort((a, b) => a.name.localeCompare(b.name));
    for (const ou of hijas) {
      // Guarda contra ciclos en el organigrama: sin ella, una OU que se declara su propia
      // ascendiente cuelga el render en un bucle infinito.
      if (visitados.has(ou.id)) continue;
      visitados.add(ou.id);
      salida.push({ ou, nivel });
      recorrer(ou.id, nivel + 1);
    }
  };

  recorrer('', 0);
  return salida;
}

// ─── WP-A3 · el interruptor GLOBAL ────────────────────────────────────────────

/**
 * WP-A3 / D-1 / CPE-117 — el toggle "Aplican puntos" de la maqueta.
 *
 * El comentario original de este fichero decía que este toggle "NO tiene contraparte real en
 * PointsConfig y se descarta a propósito". Era cierto de `PointsConfig` —que solo guarda un booleano
 * por OU+segmento— pero NO del sistema: el interruptor global es la BusinessRule
 * `POINTS_ENABLED_GLOBAL`, y el motor la consulta en cada acreditación. Existía, simplemente no
 * estaba cableado.
 *
 * Solo lo ve AdminGlobal. El backend lo exige por su cuenta (`PUT v1/rules/{code}` es
 * `AdminGlobal,AdminPulso`), así que esconderlo aquí es UX, no seguridad.
 */
function GlobalPointsToggle({ ouId, locked }: Readonly<{ ouId: string | undefined; locked: boolean }>) {
  const { t } = useTranslation('recognition');

  // 🔴 El control muestra el VALOR BASE, que es el que escribe.
  //
  // La primera versión pintaba `globalEnabled` del toggle RESUELTO —el que ya trae aplicado el
  // override de la OU en pantalla— y escribía el base. Sobre Europa, que tiene un override de
  // arranque a `true`, el admin veía ON, lo apagaba, y seguía viéndolo ON: el PUT sí hacía su
  // trabajo, pero el override ganaba. Un control que no obedece y no explica por qué.
  //
  // Además, sin OU el toggle resuelto quedaba deshabilitado y `data` era undefined, así que el
  // checkbox salía desmarcado, se anunciaba "el interruptor global está apagado" sin serlo, y desde
  // esa vista el kill-switch solo se podía ENCENDER.
  const regla = usePointsGlobalRule();
  const setGlobal = useSetGlobalPointsEnabled();

  // El resuelto se sigue consultando, pero solo para AVISAR de que en esta OU manda otra cosa.
  const resuelto = usePointsToggle(ouId, 'staff');

  // 🔴 El ESTADO DE ERROR es su propio caso, no "apagado".
  //
  // Sin esto, una regla que no carga —404 porque no está sembrada en ese ambiente, o un fallo de
  // red— dejaba `data` undefined, el checkbox desmarcado y el aviso "ninguna OU acredita puntos"
  // anunciando algo que nadie había comprobado. Peor: el control quedaba HABILITADO, así que el
  // único clic posible mandaba `true`, y el PUT CREA la regla con `value="true"` cuando no existe.
  // Es decir, se podían encender los puntos de toda la plataforma por accidente desde una pantalla
  // que afirmaba que estaban apagados.
  const noSeSabe = regla.isLoading || regla.isError;

  // `toLowerCase`: el backend lo parsea con `bool.TryParse`, que NO distingue mayúsculas. Un "True"
  // escrito por un script, por Compass o por el endpoint de overrides dejaría el checkbox apagado
  // mientras el motor lo lee encendido.
  const activo = regla.data?.isActive === true
    && regla.data?.value?.toLowerCase() === 'true';

  const overrideAqui = ouId
    ? regla.data?.ouOverrides?.some((o) => o.ouId === ouId) ?? false
    : false;

  return (
    <div className="adm-toggles-row adm-toggles-row--global">
      <label className="adm-inline-toggle adm-inline-toggle--switch" title={t('admin.points.globalToggleHint')}>
        <input
          type="checkbox"
          checked={activo && !noSeSabe}
          disabled={noSeSabe || setGlobal.isPending || locked}
          onChange={(e) => setGlobal.mutate(e.target.checked)}
        />
        {t('admin.points.globalToggle')}
      </label>

      {regla.isError && (
        <p className="adm-note" role="alert">{t('admin.points.globalUnknown')}</p>
      )}

      {!activo && !noSeSabe && (
        <p className="adm-note" role="status">{t('admin.points.globalOffHint')}</p>
      )}

      {/*
        Si esta OU tiene override, lo que se ve arriba NO decide aquí. Decirlo es la diferencia
        entre un control que parece roto y uno que se entiende.
      */}
      {overrideAqui && (
        <p className="adm-note" role="status">
          {t('admin.points.globalOverriddenHint', {
            estado: resuelto.data?.globalEnabled
              ? t('admin.points.globalOn')
              : t('admin.points.globalOff'),
          })}
        </p>
      )}

      {setGlobal.isError && (
        <p className="rp-error" role="alert">
          {getRecognitionErrorMessage(setGlobal.error, t('admin.saveError'))}
        </p>
      )}
    </div>
  );
}

interface PointsSegmentPanelProps {
  ouId: string | undefined;
  segment: PointsSegment;
  /** Bug 03 — campaña activa (por OU) resuelta en `AdminPoints`. Ver comentario ahí. */
  campaignLocked: boolean;
}

/** Una tabla completa (título + toggle "Activo" + criterios + Guardar) para UN segmento. */
/**
 * WP-A5 · RN-P09 — «Visible para empleados», POR OU.
 *
 * 🔴 Vive aquí y no en el bloque de cada segmento, y la razón es medible: la regla se administra con
 * un override de OU (`PATCH .../ou-overrides/{ouId}`), no con la config de puntos, que sí es por
 * segmento. En el bloque de segmento aparecería DOS veces —staff y frontline— mostrando y
 * escribiendo el mismo valor: un control que parece roto sin estarlo.
 *
 * 🔴 Y muestra el valor EFECTIVO DE ESTA OU —su override si lo tiene, si no el base— porque es lo
 * que escribe. Pintar el base y escribir el override es exactamente el defecto que ya tuvo el
 * interruptor global: el admin apagaba y seguía viéndolo encendido.
 */
function VisibleParaEmpleadosToggle({ ouId, locked }: Readonly<{ ouId: string; locked: boolean }>) {
  const { t } = useTranslation('recognition');

  const regla = usePointsVisibleRule();
  const setVisible = useSetOuPointsVisibility();

  // El valor ya RESUELTO por el backend, que incluye el suelo duro de GDPR. No pinta el
  // interruptor: sirve para avisar de que en esta OU la respuesta es «no» pase lo que pase.
  // Se pide sobre `staff` porque la visibilidad no depende del segmento; cualquiera de los dos
  // devuelve el mismo `visibleToEmployees`, y este es el que la pantalla ya trae en caché.
  const resuelto = usePointsConfig(ouId, 'staff');

  // El error es su propio caso, no «apagado»: una regla que no carga dejaría el checkbox
  // desmarcado afirmando algo que nadie comprobó, y el único clic posible mandaría `true`.
  const noSeSabe = regla.isLoading || regla.isError;

  const override = regla.data?.ouOverrides?.find((o) => o.ouId === ouId)?.value;
  // `toLowerCase`: el backend parsea con `bool.TryParse`, que no distingue mayúsculas. Un "True"
  // escrito por un guion o por Compass dejaría el control apagado mientras el motor lee encendido.
  const efectivo = (override ?? regla.data?.value ?? 'false').toLowerCase() === 'true'
    && regla.data?.isActive !== false;

  // Encendido aquí pero el backend dice que no ⇒ es GDPR, que es suelo duro.
  const gdprLoManda = efectivo && resuelto.data?.visibleToEmployees === false;

  return (
    <div className="adm-toggles-row adm-toggles-row--global">
      <label className="adm-inline-toggle adm-inline-toggle--switch" title={t('admin.points.visibleToEmployeesHint')}>
        <input
          type="checkbox"
          checked={efectivo && !noSeSabe}
          disabled={noSeSabe || setVisible.isPending || locked}
          onChange={(e) => setVisible.mutate({ ouId, visible: e.target.checked })}
        />
        {t('admin.points.visibleToEmployees')}
      </label>

      <p className="adm-note" role="note">{t('admin.points.visibleToEmployeesHint')}</p>

      {regla.isError && (
        <p className="adm-note" role="alert">{t('admin.points.visibleUnknown')}</p>
      )}

      {/* Decirlo es la diferencia entre un control que parece roto y uno que se entiende: el admin
          lo ha encendido y el backend sigue devolviendo «no visible». No es un fallo. */}
      {gdprLoManda && (
        <p className="adm-note" role="status">{t('admin.points.visibleGdprBlocked')}</p>
      )}

      {setVisible.isError && (
        <p className="rp-error" role="alert">
          {getRecognitionErrorMessage(setVisible.error, t('admin.saveError'))}
        </p>
      )}
    </div>
  );
}

function PointsSegmentPanel({ ouId, segment, campaignLocked }: Readonly<PointsSegmentPanelProps>) {
  const { t } = useTranslation('recognition');
  const query = usePointsConfig(ouId, segment);
  const save = useSavePointsConfig();
  const resetAccumulated = useResetPointsConfigAccumulated();
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // WP-A4 — el conteo de nominaciones por criterio. Va aparte de la config a propósito: la config se
  // EDITA y el conteo es de solo lectura, así que un fallo del conteo no puede bloquear el guardado.
  const summary = usePointsSummary(ouId, segment);

  const [draft, setDraft] = useState<PointsConfigDto | null>(null);
  const [dirty, setDirty] = useState(false);
  useUnsavedChangesGuard(dirty);

  // Solo sincroniza el borrador desde el servidor si todavía no hay uno (primera carga) o si el
  // borrador actual no tiene cambios sin guardar. Sin esta guarda, un refetch en segundo plano
  // (refetchOnWindowFocus + staleTime de 5 min, ver src/main.tsx) pisa en silencio la edición que
  // el admin todavía no guardó — mismo patrón de guarda que AdminRecap.tsx.
  //
  // 🔴 La tercera condición es de WP-A8: cambiar de OU tiene que DESCARTAR el borrador aunque tenga
  // cambios sin guardar. Sin ella, la guarda de "no pises lo que edité" conserva el draft de la OU
  // anterior y el admin acabaría guardando la config de una OU encima de otra. Se resuelve aquí y
  // no en un efecto aparte porque un efecto que anula el draft también corre en el montaje, y
  // dejaba la tabla vacía en el primer render.
  useEffect(() => {
    if (!query.data) return;
    const esOtraOu = draft !== null && draft.ouId !== query.data.ouId;
    if (draft === null || !dirty || esOtraOu) { setDraft(query.data); setDirty(false); }
  }, [query.data, draft, dirty]);

  // Bug 03 — `campaignStarted` (RulesEngine) no se descarta, se REFUERZA con la campaña activa por
  // OU (`campaignLocked`, resuelta en `AdminPoints` vía `useActiveCampaign`). Cualquiera de las dos
  // señales bloquea: así, si el backend algún día sincroniza `campaignStarted` correctamente, no se
  // pierde protección, y mientras tanto la campaña activa —la señal confiable, ver comentario en
  // `AdminPoints`— es la que de verdad decide.
  const locked = campaignLocked || (draft?.campaignStarted ?? false);

  // Índice por PAR canónico (categoría, criterio). Es el mismo lookup que hace el motor, y por la
  // misma razón: casar por la etiqueta editable es lo que dejó los puntos en cero durante meses.
  const conteos = useMemo(() => {
    const mapa = new Map<string, number>();
    for (const fila of summary.data?.nominationCountsByCriterio ?? []) {
      mapa.set(claveDelConteo(fila), fila.count);
    }
    return mapa;
  }, [summary.data]);

  const setCriterionAt = (index: number, patch: Partial<PointCriterionDto>) => {
    setDraft((current) => (current
      ? { ...current, criterios: current.criterios.map((crit, idx) => (idx === index ? { ...crit, ...patch } : crit)) }
      : current));
    setDirty(true);
  };

  const setPointsEnabled = (value: boolean) => {
    setDraft((current) => (current ? { ...current, pointsEnabled: value } : current));
    setDirty(true);
  };

  const doSave = () => {
    if (!ouId || !draft) return;
    save.mutate({ ouId, segment, body: draft }, { onSuccess: () => setDirty(false) });
  };

  // Backend (Tarea 7, revisión) — el reset queda bloqueado por la MISMA guarda que edición: el
  // backend rechaza con 422 si `PointsConfig.IsImmutable` (== `CampaignStarted`), así que el botón
  // se deshabilita con el mismo `locked` que ya deshabilita la tabla y "Guardar", no aparte.
  const doReset = () => {
    if (!ouId) return;
    resetAccumulated.mutate({ ouId, segment });
  };

  return (
    <div className="adm-points-segment">
      <h4 className="adm-subtitle">{t(`admin.points.seg.${segment}`)}</h4>

      <div className="adm-toggles-row">
        <label className="adm-inline-toggle adm-inline-toggle--switch" title={t('admin.points.segmentActiveHint')}>
          <input
            type="checkbox"
            checked={draft?.pointsEnabled ?? false}
            disabled={locked || !draft}
            onChange={(e) => setPointsEnabled(e.target.checked)}
          />
          {t('admin.points.segmentActive')}
        </label>
      </div>

      <StateBlock
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        isEmpty={!draft || draft.criterios.length === 0}
        emptyText={t('admin.points.empty')}
        onRetry={() => { query.refetch(); }}
      >
        {draft && (
          <>
            {save.isError && <p className="rp-error" role="alert">{getRecognitionErrorMessage(save.error, t('admin.saveError'))}</p>}

            <div className="adm-table-wrap">
              <table className="adm-table adm-points-table adm-table--glass">
                <thead>
                  <tr>
                    <th>{t('admin.points.colCategory')}</th>
                    <th>{t('admin.points.colCriterion')}</th>
                    <th>{t('admin.points.colPoints')}</th>
                    <th>{t('admin.points.colActive')}</th>
                    <th className="adm-num">{t('admin.points.colAccumulated')}</th>
                  </tr>
                </thead>
                <tbody>
                  {draft.criterios.map((crit, index) => (
                    <tr key={`${crit.categoria}::${crit.criterio}`}>
                      <td>{crit.categoria}</td>
                      <td>{crit.criterio}</td>
                      <td>
                        <input
                          type="number"
                          min={0}
                          max={9}
                          className="adm-points-input"
                          value={crit.puntos}
                          disabled={locked}
                          onChange={(e) => setCriterionAt(index, { puntos: Math.max(0, Math.min(9, Number(e.target.value) || 0)) })}
                        />
                      </td>
                      <td>
                        <label className="adm-inline-toggle adm-inline-toggle--switch" title={t('admin.points.criterionActiveHint')}>
                          <input
                            type="checkbox"
                            aria-label={crit.criterio}
                            checked={crit.activo}
                            disabled={locked}
                            onChange={(e) => setCriterionAt(index, { activo: e.target.checked })}
                          />
                        </label>
                      </td>
                      <td className="adm-num">
                        <ConteoCell
                          valor={conteos.get(claveDeLaFila(crit) ?? '')}
                          sinClave={claveDeLaFila(crit) === null}
                          cargando={summary.isLoading}
                          error={summary.isError}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={4}>{t('admin.points.accumulatedFooter')}</td>
                    <td className="adm-num">
                      {/*
                        🔴 El pie viene del SERVIDOR, no de sumar la columna.
                        Los criterios se solapan: una misma nominación activa origen, frecuencia y
                        breadth a la vez, así que sumar las filas la cuenta tres veces. Es
                        exactamente el defecto que tenía `TotalNominations`.
                      */}
                      <ConteoCell
                        valor={summary.data?.nominacionesEvaluadas}
                        cargando={summary.isLoading}
                        error={summary.isError}
                      />
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="adm-toggles-row">
              <button
                type="button"
                className="btn btn--secondary"
                disabled={locked || resetAccumulated.isPending}
                onClick={() => setShowResetConfirm(true)}
              >
                {t('admin.points.resetAccumulated')}
              </button>
              {resetAccumulated.isSuccess && (
                <span className="adm-note" role="status">{t('admin.points.resetAccumulatedSuccess')}</span>
              )}
              {resetAccumulated.isError && (
                <p className="rp-error" role="alert">
                  {getRecognitionErrorMessage(resetAccumulated.error, t('admin.points.resetAccumulatedError'))}
                </p>
              )}
            </div>

            {showResetConfirm && (
              <ConfirmModal
                title={t('admin.points.resetAccumulatedConfirmTitle')}
                message={t('admin.points.resetAccumulatedConfirmMessage')}
                confirmText={t('admin.points.resetAccumulated')}
                type="warning"
                onConfirm={doReset}
                onClose={() => setShowResetConfirm(false)}
              />
            )}

            {/*
              El conteo cuenta lo que el motor EVALUÓ. Con los puntos apagados no evalúa nada, así
              que la columna sale en ceros aunque haya reconocimientos: sin esta nota, el admin lee
              "0" y concluye que no hay actividad.
            */}
            {/*
              Y solo si lo observado suma CERO. El libro conserva los asientos historicos, asi que
              una OU que tuvo los puntos encendidos y hoy los tiene apagados muestra 7, 12, 4... y
              debajo un texto afirmando que todo esta en cero.
            */}
            {!draft.pointsEnabled && !summary.isLoading && (summary.data?.nominacionesEvaluadas ?? 0) === 0 && (
              <p className="adm-note" role="status">{t('admin.points.accumulatedDisabledHint')}</p>
            )}

            <AdminSaveBar
              onSave={doSave}
              saving={save.isPending}
              dirty={dirty}
              saved={save.isSuccess}
              disabledReason={locked ? t('admin.points.lockedCampaign') : null}
            />
          </>
        )}
      </StateBlock>
    </div>
  );
}

/**
 * 🔴 La clave del conteo: el par CANÓNICO, nunca la etiqueta.
 *
 * El conteo llega con las claves del motor (`origen`/`mismo_equipo`) y las filas se pintan con las
 * etiquetas editables de la maqueta (`Source of Nomination`/`Mismo equipo (…)`). Casarlas por la
 * etiqueta no encontraba nada NUNCA, así que las nueve filas mostraban 0 — indistinguible de "no
 * hubo nominaciones". Es el mismo defecto que dejó los puntos en cero durante meses, en el front.
 *
 * Se separan dos funciones a propósito, porque las dos partes tienen formas distintas: el criterio
 * de la config lleva ambas (etiqueta y clave) y el del conteo lleva solo la clave.
 */
function claveDelConteo(c: Pick<CriterioNominationCount, 'categoria' | 'criterio'>): string {
  return `${c.categoria}::${c.criterio}`;
}

/**
 * La clave de una fila de la tabla. Devuelve `null` cuando el criterio no tiene claves —config
 * anterior a la migración label→key—, y entonces la celda muestra "—": no se puede afirmar un
 * conteo de algo que no se sabe casar.
 */
function claveDeLaFila(c: PointCriterionDto): string | null {
  if (!c.categoriaKey || !c.criterioKey) return null;
  return `${c.categoriaKey}::${c.criterioKey}`;
}

interface ConteoCellProps {
  valor: number | undefined;
  cargando: boolean;
  error: boolean;
  /** El criterio no tiene claves canónicas: no se puede casar, así que no se afirma un número. */
  sinClave?: boolean;
}

/**
 * Los cuatro estados del conteo, separados a propósito: un «—» que significa a la vez "no hay
 * dato", "falló la consulta" y "no se puede calcular" no le dice nada a quien lo lee.
 */
function ConteoCell({ valor, cargando, error, sinClave = false }: Readonly<ConteoCellProps>) {
  const { t } = useTranslation('recognition');
  if (error) {
    return <span role="img" aria-label={t('admin.points.accumulatedError')}>⚠</span>;
  }
  if (cargando) return <span role="img" aria-label={t('common.loading')}>…</span>;
  if (sinClave) return <span role="img" aria-label={t('admin.points.accumulatedNoKey')}>—</span>;
  return <>{valor ?? 0}</>;
}

export default AdminPoints;
